import { useState } from "react";
import { CheckCircle, DownloadSimple, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "./ui/button";

export const publicationBlockerMessage = (readiness = {}) => {
  if (!Number(readiness.cards || 0)) return "Ainda não há resultados válidos nesta prova.";
  const blockers = readiness.blockers || {};
  const details = [
    [Number(blockers.manual_reviews || 0), "revisão(ões) manual(is)"],
    [Number(blockers.duplicates || 0), "duplicidade(s)"],
    [Number(blockers.class_issues || 0), "pendência(s) de turma"],
    [Math.max(Number(blockers.student_issues || 0), Number(blockers.unidentified_results || 0)), "aluno(s) não identificado(s)"],
  ]
    .filter(([count]) => count > 0)
    .map(([count, label]) => `${count} ${label}`);
  return details.length
    ? `Resolva antes de publicar: ${details.join(" · ")}.`
    : "Conclua as correções e os vínculos antes de publicar.";
};

export const bulletinBlockerMessage = (readiness = {}) => {
  if (!Number(readiness.linked_students || 0)) return "Não há aluno identificado com boletim elegível nesta prova.";
  const blockers = readiness.blockers || {};
  const details = [
    [Number(blockers.manual_reviews || 0), "revisão(ões) manual(is)"],
    [Number(blockers.duplicates || 0), "duplicidade(s)"],
  ]
    .filter(([count]) => count > 0)
    .map(([count, label]) => `${count} ${label}`);
  return details.length
    ? `Resolva antes de liberar os boletins: ${details.join(" · ")}.`
    : "Ainda não há boletim elegível nesta prova.";
};

export default function ExamQuickPublishButton({ exam, onPublished }) {
  const [submitting, setSubmitting] = useState("");
  const canWrite = Boolean(exam?.can_write);
  const hasAppliedResults = exam?.has_applied_results ?? Number(exam?.card_count || 0) > 0;
  const resultsPublished = Boolean(exam?.results_are_visible);
  const bulletinsPublished = Boolean(exam?.bulletins_are_visible);

  if (!hasAppliedResults) {
    return (
      <span
        data-testid={`exam-not-applied-${exam.id}`}
        className="inline-flex items-center px-3 py-2 border border-border bg-muted/20 text-muted-foreground text-xs font-mono-data uppercase tracking-widest"
      >
        Aguardando aplicação
      </span>
    );
  }

  if (!canWrite && !resultsPublished && !bulletinsPublished) return null;

  const resultStatus = resultsPublished ? (
      <span
        data-testid={`exam-published-${exam.id}`}
        className="inline-flex items-center px-3 py-2 border border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5 text-[hsl(var(--success))] text-xs font-mono-data uppercase tracking-widest"
      >
        <CheckCircle size={14} weight="bold" className="mr-2" /> Publicado
      </span>
  ) : null;

  const publish = async () => {
    setSubmitting("results");
    try {
      const statusResponse = await api.get(`/exams/${exam.id}/publication`);
      const publication = statusResponse.data || {};
      if (publication.results_are_visible) {
        onPublished?.(publication);
        toast.success("Os resultados desta prova já estão publicados");
        return;
      }
      if (!publication.readiness?.ready) {
        toast.error(publicationBlockerMessage(publication.readiness));
        return;
      }
      if (!window.confirm(
        `Publicar agora os resultados de “${exam.name}” para os alunos?\n\nOs boletins permanecerão retidos e poderão ser liberados depois.`,
      )) return;

      const response = await api.post(`/exams/${exam.id}/publication`, {
        action: "publish_results",
        release_bulletins: false,
      });
      onPublished?.(response.data);
      toast.success("Resultados publicados; boletins continuam retidos");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSubmitting("");
    }
  };

  const releaseBulletins = async () => {
    setSubmitting("bulletins");
    try {
      const statusResponse = await api.get(`/exams/${exam.id}/publication`);
      const publication = statusResponse.data || {};
      if (!publication.results_are_visible) {
        onPublished?.(publication);
        toast.error("Publique os resultados antes de liberar os boletins");
        return;
      }
      if (publication.bulletins_are_visible) {
        onPublished?.(publication);
        toast.success("Os boletins desta prova já estão liberados");
        return;
      }
      if (!publication.bulletin_readiness?.ready) {
        toast.error(bulletinBlockerMessage(publication.bulletin_readiness));
        return;
      }
      const ignored = publication.bulletin_readiness.ignored_blockers || {};
      const ignoredTotal = Number(ignored.total || 0);
      const ignoredNote = ignoredTotal
        ? `\n\n${ignoredTotal} pendência(s) de turma ou identificação serão ignoradas. Os boletins serão liberados somente para alunos identificados.`
        : "";
      if (!window.confirm(
        `Liberar agora os boletins individuais de “${exam.name}” para os alunos?${ignoredNote}`,
      )) return;

      const response = await api.post(`/exams/${exam.id}/publication`, {
        action: "release_bulletins",
      });
      onPublished?.(response.data);
      toast.success("Boletins liberados para os alunos identificados");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSubmitting("");
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {resultStatus}
      {!resultsPublished && canWrite && (
        <Button
          type="button"
          data-testid={`exam-publish-${exam.id}`}
          variant="outline"
          disabled={Boolean(submitting)}
          onClick={publish}
          className="rounded-none border-[hsl(var(--success))] text-[hsl(var(--success))] hover:bg-[hsl(var(--success))] hover:text-white font-mono-data uppercase tracking-widest text-xs"
        >
          {submitting === "results"
            ? <Spinner size={14} className="mr-2 animate-spin" />
            : <CheckCircle size={14} weight="bold" className="mr-2" />}
          {submitting === "results" ? "Conferindo…" : "Publicar resultados"}
        </Button>
      )}
      {bulletinsPublished ? (
        <span
          data-testid={`exam-bulletins-published-${exam.id}`}
          className="inline-flex items-center px-3 py-2 border border-primary/50 bg-primary/5 text-primary text-xs font-mono-data uppercase tracking-widest"
        >
          <CheckCircle size={14} weight="bold" className="mr-2" /> Boletins liberados
        </span>
      ) : resultsPublished && canWrite ? (
        <Button
          type="button"
          data-testid={`exam-release-bulletins-${exam.id}`}
          variant="outline"
          disabled={Boolean(submitting)}
          onClick={releaseBulletins}
          className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"
        >
          {submitting === "bulletins"
            ? <Spinner size={14} className="mr-2 animate-spin" />
            : <DownloadSimple size={14} weight="bold" className="mr-2" />}
          {submitting === "bulletins" ? "Conferindo…" : "Liberar boletins"}
        </Button>
      ) : null}
    </div>
  );
}
